import {
  Map as MaplibreMap,
  GeoJSONSource,
  type ExpressionSpecification,
} from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import { MAP_CONFIG } from "./config";
import { deduplicateFeatures, debounce, getBounds } from "./utils";
import { poiStore } from "$lib/stores/pois.svelte";
import { detailsStore } from "$lib/stores/details.svelte";
import { stateStore } from "$lib/stores/state.svelte";
import { locationStore } from "$lib/stores/location.svelte";
import type { PoiItem } from "$lib/types";
import { IsMobile } from "$lib/helpers/is-mobile.svelte";

export class MapManager {
  private map!: MaplibreMap;
  private debouncedMoveHandler!: () => void;
  private loadedIcons = new Set<string>();
  private iconLoadingPromises = new Map<string, Promise<void>>();
  private couponImage: HTMLImageElement | null = null;

  private iconCache = new Map<string, ImageBitmap>();
  private iconLoadQueue = new Set<string>();
  private isProcessingQueue = false;
  private readonly MAX_CONCURRENT_LOADS = 6;
  private readonly canvas: HTMLCanvasElement;
  private readonly ctx: CanvasRenderingContext2D;

  private startCenter: [number, number];
  private startZoom: number;

  constructor(
    private mapElement: HTMLDivElement,
    center: [number, number] = MAP_CONFIG.budapest.center,
    zoom: number = MAP_CONFIG.budapest.zoom
  ) {
    // Reuse single canvas for all icon processing
    this.canvas = document.createElement("canvas");
    this.canvas.width = 64;
    this.canvas.height = 64;
    this.ctx = this.canvas.getContext("2d", {
      willReadFrequently: false,
      alpha: true,
    })!;
    this.startCenter = center;
    this.startZoom = zoom;

    this.debouncedMoveHandler = debounce(() => {
      this.handleMapMove();
    }, MAP_CONFIG.debounceTimeout);
  }

  async initialize(): Promise<void> {
    const center = this.startCenter;
    const zoom = this.startZoom;

    const primaryStyle = import.meta.env.VITE_STYLE_URL;
    const fallbackStyle =
      "https://basemaps.cartocdn.com/gl/voyager-gl-style/style.json";

    try {
      // try loading the primary style
      this.map = await this.loadMapWithStyle(primaryStyle, center, zoom);
    } catch (error) {
      console.warn(
        `[Maplibre] Failed to load primary style (${primaryStyle}). Falling back.`,
        error
      );
      this.map = await this.loadMapWithStyle(fallbackStyle, center, zoom);
    }

    if (this.map.setRenderWorldCopies) {
      this.map.setRenderWorldCopies(false);
    }

    await this.preloadUserLocationIcon();
    await this.preloadCouponIcon();

    locationStore.setMapInstance(this.map);

    this.setupDataSources();
    this.setupLayers();
    this.setupEventListeners();
  }

  /**
   * Helper — initializes a maplibre map with error handling.
   */
  private loadMapWithStyle(
    style: string,
    center: [number, number],
    zoom: number
  ): Promise<MaplibreMap> {
    return new Promise((resolve, reject) => {
      const map = new MaplibreMap({
        container: this.mapElement,
        style,
        center,
        zoom,
        attributionControl: { compact: true },
      });

      const onLoad = () => {
        cleanup();
        resolve(map);
      };

      const onError = (e: unknown) => {
        cleanup();
        map.remove(); // prevent ghost map objects
        reject(e instanceof Error ? e : new Error(String(e)));
      };

      const cleanup = () => {
        map.off("load", onLoad);
        map.off("error", onError);
      };

      map.on("load", onLoad);
      map.on("error", onError);
    });
  }

  private setupDataSources(): void {
    // Add empty GeoJSON source for POIs
    this.map.addSource("pois", {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features: [],
      },
      cluster: true,
      clusterMaxZoom: MAP_CONFIG.cluster.clusterMaxZoom,
      clusterRadius: MAP_CONFIG.cluster.clusterRadius,
    });

    this.map.addSource("user-location", {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features: [],
      },
      // No clustering for user location
    });

    // Subscribe to POI store changes
    poiStore.subscribeToMapLibre(async (features) => {
      if (!this.map || !this.map.loaded()) return;

      // Preload icons for a small subset to avoid flooding when zoomed out.
      const preloadSample =
        features.length > 500 ? features.slice(0, 500) : features;
      this.preloadIconsForFeatures(preloadSample);

      // Update map immediately
      const source = this.map.getSource("pois") as GeoJSONSource;
      if (source) {
        source.setData({
          type: "FeatureCollection",
          features: features,
        });
      }
    });

    // Trigger initial load of POIs for current viewport
    setTimeout(() => {
      this.handleMapMove();
    }, 100);
  }

  private async setupLayers(): Promise<void> {
    // Add clustered points layer
    this.map.addLayer({
      id: "clusters",
      type: "circle",
      source: "pois",
      filter: ["has", "point_count"],
      paint: {
        "circle-color": "#FDE390",
        "circle-radius": [
          "step",
          ["get", "point_count"],
          20,
          10,
          25,
          50,
          30,
          100,
          35,
        ],
        "circle-stroke-width": 1,
        "circle-stroke-color": "#41580a80",
      },
    });

    // Add cluster count labels
    this.map.addLayer({
      id: "cluster-count",
      type: "symbol",
      source: "pois",
      filter: ["has", "point_count"],
      layout: {
        "text-field": "{point_count_abbreviated}",
        "text-font": ["Open Sans Regular", "Arial Unicode MS Regular"],
        "text-size": 12,
      },
      paint: {
        "text-color": "#3E330D",
      },
    });

    this.map.addLayer({
      id: "highlighted-point-shadow",
      type: "circle",
      source: "pois",
      filter: ["==", ["get", "id"], -1], // Start with no matches
      paint: {
        "circle-radius": 30,
        "circle-color": "rgba(0, 0, 0, 0.2)",
        "circle-blur": 1.5,
        "circle-stroke-width": 0,
      },
    });

    const baseIconExpression: ExpressionSpecification = [
      "case",
      // Condition: if 'waterableStatus' exists and is not literally null
      [
        "all",
        ["has", "waterableStatus"],
        ["!=", ["get", "waterableStatus"], ["literal", null]],
      ],
      // Result: use the waterable status icon
      [
        "concat",
        "plant_waterable-",
        ["downcase", ["get", "waterableStatus"]],
        "-icon",
      ],
      [
        "concat",
        ["coalesce", ["get", "subCategory"], "unknown"],
        "-",
        ["coalesce", ["get", "label"], "unknown"],
        "-icon",
      ],
    ];

    const iconImageExpression: ExpressionSpecification = [
      "case",
      ["==", ["get", "hasCoupon"], true],
      ["concat", baseIconExpression, "-coupon"],
      baseIconExpression,
    ];

    // Regular unclustered point layer
    this.map.addLayer({
      id: "unclustered-point",
      type: "symbol",
      source: "pois",
      filter: [
        "all",
        ["!", ["has", "point_count"]],
        ["!=", ["get", "id"], "user-location"], // Exclude user location
      ],
      minzoom: MAP_CONFIG.cluster.clusterMaxZoom,
      layout: {
        "icon-image": iconImageExpression,
        "icon-size": 1.5,
        "icon-allow-overlap": true,
        "icon-ignore-placement": true,
      },
    });

    // Highlighted icon layer (appears on top)
    this.map.addLayer({
      id: "highlighted-point",
      type: "symbol",
      source: "pois",
      filter: ["==", ["get", "id"], -1], // Start with no matches
      minzoom: MAP_CONFIG.cluster.clusterMaxZoom,
      layout: {
        "icon-image": iconImageExpression,
        "icon-size": 2.5,
        "icon-allow-overlap": true,
        "icon-ignore-placement": true,
      },
    });

    this.setupUserLocationLayer();
    // Set up dynamic icon loading
  }

  private setupUserLocationLayer(): void {
    this.map.addLayer({
      id: "user-location-layer",
      type: "symbol",
      source: "user-location", // Use separate source
      layout: {
        "icon-image": "user_location-USER_LOCATION-icon",
        "icon-size": 1.2,
        "icon-allow-overlap": true,
        "icon-ignore-placement": true,
      },
    });
  }

  private async preloadIconsForFeatures(features: any[]): Promise<void> {
    const iconsNeeded = new Set<string>();

    // Collect all needed icons
    for (const feature of features) {
      const props = feature.properties;
      if (!props) continue;

      let iconId: string | null = null;

      if (props.waterableStatus) {
        iconId = `plant_waterable-${props.waterableStatus.toLowerCase()}-icon`;
      } else if (props.subCategory && props.label) {
        iconId = `${props.subCategory}-${props.label}-icon`;
      }

      if (iconId && props.hasCoupon) {
        iconId += "-coupon";
      }

      if (
        iconId &&
        !this.loadedIcons.has(iconId) &&
        !this.map.hasImage(iconId)
      ) {
        iconsNeeded.add(iconId);
      }
    }

    if (iconsNeeded.size === 0) return;

    iconsNeeded.forEach((id) => this.iconLoadQueue.add(id));
    this.processIconQueue();
    return Promise.resolve();
  }

  private async processIconQueue(): Promise<void> {
    if (this.isProcessingQueue || this.iconLoadQueue.size === 0) return;

    this.isProcessingQueue = true;

    // Process in batches
    const batch = Array.from(this.iconLoadQueue).slice(
      0,
      this.MAX_CONCURRENT_LOADS
    );
    batch.forEach((id) => this.iconLoadQueue.delete(id));

    // Load batch concurrently
    await Promise.allSettled(
      batch.map((iconId) => this.loadIconOptimized(iconId))
    );

    this.isProcessingQueue = false;

    // Process next batch if there are more
    if (this.iconLoadQueue.size > 0) {
      // Use requestIdleCallback for non-critical icons
      if ("requestIdleCallback" in window) {
        requestIdleCallback(() => this.processIconQueue(), { timeout: 100 });
      } else {
        setTimeout(() => this.processIconQueue(), 16);
      }
    }
  }

  private async loadIconOptimized(iconId: string): Promise<void> {
    if (this.iconCache.has(iconId)) {
      const bitmap = this.iconCache.get(iconId)!;
      if (!this.map.hasImage(iconId)) {
        this.map.addImage(iconId, bitmap, { pixelRatio: 2 });
        this.loadedIcons.add(iconId);
      }
      return;
    }

    try {
      const hasCoupon = iconId.endsWith("-coupon");
      const baseIconId = hasCoupon ? iconId.replace("-coupon", "") : iconId;

      if (baseIconId.startsWith("plant_waterable-")) {
        const status = baseIconId
          .replace("plant_waterable-", "")
          .replace("-icon", "");
        const iconPath = `/assets/icons/plant_waterable/${status}.svg`;
        const bitmap = await this.loadIconFromPath(iconPath, hasCoupon);
        this.cacheAndAddIcon(iconId, bitmap);
        return;
      }

      const match = baseIconId.match(/^(.+?)-(.+?)-icon$/);
      if (!match) return;

      const [_, subCategory, label] = match;
      const iconPath = await this.findIconPath(subCategory, label);

      if (iconPath) {
        const bitmap = await this.loadIconFromPath(iconPath, hasCoupon);
        this.cacheAndAddIcon(iconId, bitmap);
      } else {
        const bitmap = await this.createOptimizedDefaultIcon(label, hasCoupon);
        this.cacheAndAddIcon(iconId, bitmap);
      }
    } catch (error) {
      // Silently create default icon on any error
      const bitmap = await this.createOptimizedDefaultIcon(
        undefined,
        iconId.endsWith("-coupon")
      );
      this.cacheAndAddIcon(iconId, bitmap);
    }
  }

  private async findIconPath(
    subCategory: string,
    label: string
  ): Promise<string | null> {
    // Check which icon exists using HEAD requests (lighter than loading the full image)
    const paths = [
      `/assets/icons/${subCategory.toLowerCase()}.svg`,
      `/assets/icons/${label.toLowerCase()}.svg`,
    ];

    for (const path of paths) {
      try {
        const res = await fetch(path, { method: "HEAD" });
        if (
          res.ok &&
          res.headers.get("Content-Type")?.includes("image/svg+xml")
        )
          return path;
      } catch {
        continue;
      }
    }

    return null;
  }

  private async loadIconFromPath(
    src: string,
    hasCoupon: boolean
  ): Promise<ImageBitmap> {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";

      img.onload = async () => {
        // Clear and reuse canvas
        this.ctx.clearRect(0, 0, 64, 64);

        // Add shadow
        this.ctx.shadowOffsetX = 3;
        this.ctx.shadowOffsetY = 3;
        this.ctx.shadowBlur = 2;
        this.ctx.shadowColor = "black";

        const targetSize = 64;
        const padding = 6; // Add padding to prevent shadow clipping
        const maxDrawSize = targetSize - padding * 2;

        const aspect = img.width / img.height;
        let drawWidth = maxDrawSize;
        let drawHeight = maxDrawSize;

        if (aspect > 1) {
          drawHeight = maxDrawSize / aspect;
        } else {
          drawWidth = maxDrawSize * aspect;
        }

        // Center with padding compensation
        this.ctx.drawImage(
          img,
          padding + (maxDrawSize - drawWidth) / 2,
          padding + (maxDrawSize - drawHeight) / 2,
          drawWidth,
          drawHeight
        );

        if (hasCoupon && this.couponImage) {
          const couponSize = 24;
          const couponX = targetSize - couponSize;
          const couponY = targetSize - couponSize;

          // Draw coupon shadow
          this.ctx.shadowColor = "rgba(0,0,0,0.3)";
          this.ctx.shadowBlur = 2;
          this.ctx.drawImage(
            this.couponImage,
            couponX,
            couponY,
            couponSize,
            couponSize
          );
        }

        // Create bitmap directly from canvas
        const bitmap = await createImageBitmap(this.canvas, {
          premultiplyAlpha: "premultiply",
          colorSpaceConversion: "none",
        });
        resolve(bitmap);
      };

      img.onerror = () => reject(new Error(`Failed to load: ${src}`));
      img.src = src;
    });
  }

  private async createOptimizedDefaultIcon(
    label?: string,
    hasCoupon?: boolean
  ): Promise<ImageBitmap> {
    const size = 64;
    const colors: Record<string, string> = {
      STORE: "#10B981",
      RESTAURANT: "#F59E0B",
      RENTAL: "#3B82F6",
      REPAIR: "#8B5CF6",
      WASTE: "#EF4444",
      STATION: "#06B6D4",
      COMMON_GOOD: "#EC4899",
      EVENT: "#F97316",
      SUCCESS_STORY: "#84CC16",
    };

    const color = colors[label || ""] || "#6B7280";

    // Clear and reuse canvas
    this.ctx.clearRect(0, 0, size, size);

    // Add shadow
    this.ctx.shadowOffsetX = 2;
    this.ctx.shadowOffsetY = 2;
    this.ctx.shadowBlur = 2;
    this.ctx.shadowColor = "black";

    // Use simpler shape for better performance
    this.ctx.fillStyle = color;
    this.ctx.beginPath();
    this.ctx.arc(size / 2, size / 2, size / 3, 0, Math.PI * 2);
    this.ctx.fill();

    // Reset shadow for inner dot
    this.ctx.shadowColor = "transparent";
    this.ctx.shadowOffsetX = 0;
    this.ctx.shadowOffsetY = 0;
    this.ctx.shadowBlur = 0;

    // Simple white dot in center
    this.ctx.fillStyle = "rgba(255, 255, 255, 0.9)";
    this.ctx.arc(size / 2, size / 2, size / 6, 0, Math.PI * 2);
    this.ctx.fill();

    if (hasCoupon && this.couponImage) {
      const couponSize = 24;
      const couponX = size - couponSize;
      const couponY = size - couponSize;

      this.ctx.shadowColor = "rgba(0,0,0,0.3)";
      this.ctx.shadowBlur = 2;
      this.ctx.drawImage(
        this.couponImage,
        couponX,
        couponY,
        couponSize,
        couponSize
      );
    }

    return createImageBitmap(this.canvas, {
      premultiplyAlpha: "premultiply",
      colorSpaceConversion: "none",
    });
  }

  private cacheAndAddIcon(iconId: string, bitmap: ImageBitmap): void {
    this.iconCache.set(iconId, bitmap);

    if (!this.map.hasImage(iconId)) {
      this.map.addImage(iconId, bitmap, { pixelRatio: 2 });
      this.loadedIcons.add(iconId);
    }
  }

  private async preloadUserLocationIcon(): Promise<void> {
    const iconId = "user_location-USER_LOCATION-icon";

    if (this.map.hasImage(iconId)) {
      return;
    }

    try {
      const img = new Image();
      const iconLoaded = new Promise<void>((resolve, reject) => {
        img.onload = async () => {
          try {
            const canvas = document.createElement("canvas");
            const size = 64;
            canvas.width = size;
            canvas.height = size;

            const ctx = canvas.getContext("2d");
            if (!ctx) {
              reject(new Error("Could not get canvas context"));
              return;
            }

            ctx.drawImage(img, 0, 0, size, size);
            const bitmap = await createImageBitmap(canvas);

            if (!this.map.hasImage(iconId)) {
              this.map.addImage(iconId, bitmap, { pixelRatio: 2 });
            }
            resolve();
          } catch (error) {
            reject(error);
          }
        };

        img.onerror = () => reject(new Error("Failed to load butterfly icon"));
        img.src = "/assets/bee.svg";
      });

      await iconLoaded;
    } catch (error) {
      console.error("Failed to preload user location icon:", error);
    }
  }

  private async preloadCouponIcon(): Promise<void> {
    try {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = "/assets/icons/coupon.svg";
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
      });
      this.couponImage = img;
    } catch (e) {
      console.warn("Failed to load coupon icon", e);
    }
  }

  private setupEventListeners(): void {
    this.map.on("moveend", () => {
      this.debouncedMoveHandler();
    });
    this.map.on("zoomend", () => {
      this.debouncedMoveHandler();
    });

    // Handle clicks on clusters with explosion effect
    this.map.on("click", "clusters", async (e) => {
      const features = this.map.queryRenderedFeatures(e.point, {
        layers: ["clusters"],
      });

      if (features.length === 0) return;

      const clusterId = features[0].properties.cluster_id;
      const source = this.map.getSource("pois") as GeoJSONSource;
      const clusterCoordinates = (features[0].geometry as any).coordinates;

      // Get all features in the cluster
      try {
        const clusterFeatures = await source.getClusterChildren(clusterId);

        const pois = clusterFeatures
          .filter((f) => f.properties && !f.properties.cluster)
          .map((f) => f.properties);

        if (pois.length > 0) {
          let { lat, lng } = pois.reduce(
            (acc: { lat: number; lng: number }, poi) => {
              acc.lat += poi?.latitude || 0;
              acc.lng += poi?.longitude || 0;
              return acc;
            },
            { lat: 0, lng: 0 }
          );
          lat /= pois.length;
          lng /= pois.length;

          if (new IsMobile().current) {
            stateStore.activePage = null;
          } else {
            detailsStore.select(pois as PoiItem[]);
            stateStore.openDetail();
            lng -= 0.005;
          }

          locationStore.centerMapOn({ lat: lat, lng: lng }, 15);
        }
      } catch (error) {
        // @ts-ignore
        source.getClusterExpansionZoom(clusterId, (err, zoom) => {
          if (err) return;

          this.map.easeTo({
            center: clusterCoordinates,
            zoom: zoom,
            duration: 300,
          });
        });
      }
    });

    // Handle clicks on unclustered points
    this.map.on("click", ["unclustered-point", "highlighted-point"], (e) => {
      if (!e.features || e.features.length === 0) return;

      const features = this.map
        .queryRenderedFeatures(e.point, {
          layers: ["unclustered-point", "highlighted-point"],
        })
        .filter((f) => f.properties?.id !== "user-location"); // Filter out user location

      const pois = deduplicateFeatures(features).map((feat) => feat.properties);

      if (!pois.length) return;
      detailsStore.select(pois).then(() => {
        this.handleCenterClick(
          detailsStore.currentPoiDetail?.latitude,
          detailsStore.currentPoiDetail?.longitude,
          locationStore?.selectedPoiId !==
            detailsStore?.currentPoiDetail?.id.toString()
        );
      });
      stateStore.openDetail();
    });

    // Change cursor on hover
    this.map.on("mouseenter", "clusters", () => {
      this.map.getCanvas().style.cursor = "pointer";
    });

    this.map.on("mouseleave", "clusters", () => {
      this.map.getCanvas().style.cursor = "";
    });

    this.map.on(
      "mouseenter",
      ["unclustered-point", "highlighted-point"],
      () => {
        this.map.getCanvas().style.cursor = "pointer";
      }
    );

    this.map.on(
      "mouseleave",
      ["unclustered-point", "highlighted-point"],
      () => {
        this.map.getCanvas().style.cursor = "";
      }
    );
  }

  private handleCenterClick(
    lat: number | undefined,
    lng: number | undefined,
    closePageMobile: boolean
  ) {
    if (!lat || !lng) return;

    const poiId = detailsStore.currentPoiDetail?.id?.toString();

    // Set the selected POI ID for highlighting
    locationStore.setSelectedPoi(poiId || null);

    // on mobile, let's close the drawer, on desktop, slightly center is to the right
    if (new IsMobile().current) {
      if (closePageMobile) stateStore.activePage = null;
    } else {
      lng -= 0.0007;
    }
    locationStore.centerMapOn({ lat, lng }, 18);
  }

  private handleMapMove(): void {
    const bounds = getBounds(this.map);

    poiStore.setVisibleBounds(bounds);
    poiStore.loadPoisForMapCoordinates(
      bounds.north,
      bounds.east,
      bounds.south,
      bounds.west,
      this.map.getZoom()
    );
  }

  getMap(): MaplibreMap {
    return this.map;
  }

  destroy(): void {
    // Clear icon loading promises
    this.iconLoadingPromises.clear();
    this.loadedIcons.clear();

    if (this.map) {
      this.map.remove();
    }
  }
}
